import React from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Grid,
  Typography,
  colors,
  CardActionArea,
  Link,
  Tooltip,
} from '@material-ui/core';
import { CardBase } from 'components/organisms';
import { Image } from 'components/atoms';
import { ActionButton } from 'components/atoms';

const useStyles = makeStyles(theme => ({
  root: {
    height: '100%',
    width: '100%',
    backgroundColor: '#ECEBEB9D',
    borderRadius: theme.spacing(3),
    '&card-base__content:last-child': {
      padding: theme.spacing(1, 2),
    },
    [theme.breakpoints.up('md')]: {
      '&card-base__content:last-child': {
        padding: theme.spacing(1.6, 2.5),
      },
    },
    '&.selected': {
      backgroundColor: '#D4D4D49D',
    },
  },
  actionArea: {
    padding: theme.spacing(2, 4),
    [theme.breakpoints.up('md')]: {
      '&:last-child': {
        padding: theme.spacing(1.6, 2.5),
      },
    },
  },
  content: {
    padding: 0,
    '&:last-child': {
      padding: 0,
    },
    [theme.breakpoints.up('md')]: {
      padding: 0,
      '&:last-child': {
        padding: 0,
      },
    },
  },
  title: {
    fontSize: 16,
    fontFamily: 'Roboto',
    color: '#1A1818',
    lineHeight: 'initial',
    marginBottom: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  subtitle: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: 14,
    fontFamily: 'Roboto',
    color: '#1A1818',
    lineHeight: 'initial',
  },
  categoryIconButton: {
    display: 'flex',
    width: '100%',
    justifyContent: 'flex-end',
  },
  button: {
    height: 25,
    paddingRight: 12,
    paddingLeft: 12,
    marginLeft: 12,
    fontWeight: 'normal',
    fontSize: 11,
  },
  imageContainer: {
    width: 30,
    height: 40,
    marginRight: theme.spacing(2.5),
    [theme.breakpoints.up('md')]: {
      marginRight: theme.spacing(2),
    },
  },
  image: {},
  infoContainer: {
    width: `calc(100% - ${43 + 83 + theme.spacing(3)}px)`,
    [theme.breakpoints.up('md')]: {
      width: `calc(100% - ${43 + 83 + theme.spacing(2.5)}px)`,
    },
  },
}));

/**
 * Component to display the category link card
 *
 * @param {Object} props
 */
const CardDocument = props => {
  const {
    fontIconClass,
    color,
    title,
    subtitle,
    href,
    align,
    className,
    iconAlternateProps,
    imageUrl,
    titleProps,
    subtitleProps,
    selected,
    downloadText = '',
    ...rest
  } = props;

  const classes = useStyles();

  let justifyGrid = 'flex-start';
  if (align === 'center') {
    justifyGrid = 'center';
  } else if (align === 'right') {
    justifyGrid = 'flex-end';
  }
  return (
    <CardBase
      className={clsx('card-category-link', classes.root, className, {
        selected,
      })}
      withShadow={false}
      elevation={0}
      classes={{
        content: classes.content,
      }}
      {...rest}
    >
      <CardActionArea className={classes.actionArea}>
        <Grid
          container
          justify={justifyGrid}
          alignItems="center"
          spacing={0}
          className="card-category-link__wrapper"
        >
          <Grid item className={classes.imageContainer}>
            <Image
              src={imageUrl}
              alt="document"
              className={classes.image}
              lazyProps={{
                height: '40px',
              }}
            />
          </Grid>
          <Grid item className={classes.infoContainer}>
            <Tooltip title={title}>
              <Typography
                variant="h6"
                align={align}
                className={classes.title}
                {...titleProps}
              >
                {title}
              </Typography>
            </Tooltip>
            {subtitle && (
              <Tooltip title={subtitle}>
                <Typography
                  className={classes.subtitle}
                  variant="subtitle1"
                  align={align}
                  {...subtitleProps}
                >
                  {subtitle}
                </Typography>
              </Tooltip>
            )}
          </Grid>
          <Grid item className={classes.buttonContainer}>
            <ActionButton
              variant="contained"
              color="primary"
              size="large"
              className={clsx(classes.button)}
              component={Link}
              // to={props.href}
              // target="_blank"
              rel="noopener noreferrer"
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                window.open(props.href);
              }}
            >
              {downloadText}
            </ActionButton>
          </Grid>
        </Grid>
      </CardActionArea>
    </CardBase>
  );
};

CardDocument.defaultProps = {
  align: 'left',
  href: '#',
  iconAlternateProps: {},
  titleProps: {},
  subtitleProps: {},
};

CardDocument.propTypes = {
  /**
   * External classes
   */
  className: PropTypes.string,
  /**
   * Promo title to show inside the card
   */
  title: PropTypes.string.isRequired,
  /**
   * Promo subtitle to show inside the card
   */
  subtitle: PropTypes.string,
  /**
   * Promo description to show inside the card
   */
  href: PropTypes.string.isRequired,
  /**
   * Promo font icon class name to show inside the card
   */
  fontIconClass: PropTypes.string,
  /**
   * Promo icon color to show inside the card
   */
  color: PropTypes.oneOf([
    colors.red,
    colors.pink,
    colors.purple,
    colors.deepPurple,
    colors.indigo,
    colors.blue,
    colors.lightBlue,
    colors.cyan,
    colors.teal,
    colors.green,
    colors.lightGreen,
    colors.lime,
    colors.yellow,
    colors.amber,
    colors.orange,
    colors.deepOrange,
    colors.brown,
    colors.grey,
    colors.blueGrey,
  ]).isRequired,
  /**
   * The content alignment
   */
  align: PropTypes.oneOf(['left', 'right', 'center']),
  /**
   * Additional props to pass to the IconAlternate component
   */
  iconAlternateProps: PropTypes.object,
  /**
   * Additional props to pass to the title Typography component
   */
  titleProps: PropTypes.object,
  /**
   * Additional props to pass to the subtitle Typography component
   */
  subtitleProps: PropTypes.object,
  /**
   * Add helper css class to UI if selected is true
   */
  selected: PropTypes.bool,
  /**
   * Url of the image to show
   */
  imageUrl: PropTypes.string,
  /**
   * DownloadText of the button to show
   */
  downloadText: PropTypes.string,
};

export default CardDocument;
